/**
 * Typed client for the Mynstra BIM REST API (DESIGN §6.1). Works in Node 22 and browsers (global fetch).
 * Authenticate with an API key (`Authorization: Bearer p2b_…`) or, in the web app, the session cookie.
 */
import type {
  AccessGrant,
  AssignUserRequest,
  CreateInvitationRequest,
  CreateOrgDomainRequest,
  InvitationView,
  OrgDomain,
  PendingUser,
  AuditEvent,
  BeginUploadRequest,
  BeginUploadResponse,
  CompleteUploadRequest,
  CompleteUploadResponse,
  CreateOrgRequest,
  CreateProjectRequest,
  FileEntry,
  FileVersion,
  ListFilesQuery,
  Me,
  Org,
  PatchFileRequest,
  Project,
  ProjectListItem,
  Publication,
  PublishRequest,
  PutAccessRequest,
  Revision,
  RevisionManifestInput,
  Via,
  PageSession,
  OrgMembersView,
  OrgProject,
  UpdateMemberRequest,
  UpdateOrgRequest,
  UpdateProjectRequest,
  AddressQuery,
  RegistryAddress,
} from '@p2b/contract';

export class P2bError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'P2bError';
  }
}

export interface PublicationView extends Publication {
  revisionLabel: string;
  downloads: { path: string; size: number; kind: string }[];
}

export interface ApiKeyInfo {
  id: string;
  name: string | null;
  start: string | null;
  scope: 'read' | 'write';
  projects: string[] | null;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  enabled: boolean;
}

export interface CreatedApiKey {
  id: string;
  key: string;
  name: string;
  scope: 'read' | 'write';
  projects: string[] | null;
  expiresAt: string | null;
}

export type OrgMembers = OrgMembersView;

export interface DownloadInfo {
  url: string;
  file: FileEntry;
  version: FileVersion;
  expiresInSeconds: number;
}

/** Bytes to upload: the CLI reads file ranges, the browser slices a Blob. */
export interface ByteSource {
  size: number;
  read(start: number, end: number): Promise<Uint8Array>;
}

export interface ClientOptions {
  /** Platform origin, e.g. https://bim.mynstra.is (no trailing slash needed). */
  baseUrl: string;
  apiKey?: string;
  /** Recorded in the audit log as `via` for API-key calls (default `cli`). */
  client?: Via;
  fetch?: typeof fetch;
  /** Browser: send the session cookie. */
  credentials?: RequestInit['credentials'];
}

/** Encode a workspace path for a URL, segment by segment. */
export function encodePath(path: string): string {
  return path
    .split('/')
    .map((s) => encodeURIComponent(s))
    .join('/');
}

export function encodeRef(ref: string): string {
  return ref.replace('/', '~');
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}

export class P2bClient {
  readonly baseUrl: string;
  private readonly apiKey?: string;
  private readonly via: Via;
  private readonly doFetch: typeof fetch;
  private readonly credentials?: RequestInit['credentials'];

  constructor(opts: ClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.apiKey = opts.apiKey;
    this.via = opts.client ?? 'cli';
    this.doFetch = opts.fetch ?? ((...args) => fetch(...args));
    this.credentials = opts.credentials;
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return {
      ...(this.apiKey ? { authorization: `Bearer ${this.apiKey}`, 'x-p2b-client': this.via } : {}),
      ...extra,
    };
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await this.doFetch(this.baseUrl + path, {
      method,
      headers: this.headers(body !== undefined ? { 'content-type': 'application/json' } : {}),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: this.credentials,
      redirect: 'manual',
    });
    const text = await res.text();
    let data: any = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }
    if (!res.ok) {
      const err = data && typeof data === 'object' && data.error ? data.error : null;
      throw new P2bError(
        res.status,
        err?.code ?? `http_${res.status}`,
        err?.message ?? `${method} ${path} failed with ${res.status}`,
        err?.details,
      );
    }
    return data as T;
  }

  // --- account -------------------------------------------------------------------------------
  me() {
    return this.request<Me>('GET', '/api/me');
  }
  async orgs() {
    return (await this.request<{ orgs: Org[] }>('GET', '/api/orgs')).orgs;
  }
  createOrg(input: CreateOrgRequest) {
    return this.request<Org>('POST', '/api/orgs', input);
  }
  orgMembers(org: string) {
    return this.request<OrgMembers>('GET', `/api/orgs/${encodeURIComponent(org)}/members`);
  }
  updateOrg(org: string, req: UpdateOrgRequest) {
    return this.request<Org>('PATCH', `/api/orgs/${encodeURIComponent(org)}`, req);
  }
  async orgProjects(org: string) {
    return (
      await this.request<{ projects: OrgProject[] }>('GET', `/api/orgs/${encodeURIComponent(org)}/projects`)
    ).projects;
  }
  setMemberRole(org: string, userId: string, req: UpdateMemberRequest) {
    return this.request<{ ok: true; role: string }>(
      'PATCH',
      `/api/orgs/${encodeURIComponent(org)}/members/${encodeURIComponent(userId)}`,
      req,
    );
  }
  removeMember(org: string, userId: string) {
    return this.request<{ ok: true }>(
      'DELETE',
      `/api/orgs/${encodeURIComponent(org)}/members/${encodeURIComponent(userId)}`,
    );
  }
  async keys() {
    return (await this.request<{ keys: ApiKeyInfo[] }>('GET', '/api/keys')).keys;
  }
  createKey(input: {
    name: string;
    scope?: 'read' | 'write';
    projects?: string[] | null;
    expiresInDays?: number | null;
  }) {
    return this.request<CreatedApiKey>('POST', '/api/keys', input);
  }
  deleteKey(id: string) {
    return this.request<{ ok: true }>('DELETE', `/api/keys/${encodeURIComponent(id)}`);
  }

  // --- onboarding (DESIGN §2) ---------------------------------------------------------------
  createInvitation(org: string, req: CreateInvitationRequest) {
    return this.request<InvitationView>('POST', `/api/orgs/${encodeURIComponent(org)}/invitations`, req);
  }
  async invitations(org: string) {
    return (
      await this.request<{ invitations: InvitationView[] }>(
        'GET',
        `/api/orgs/${encodeURIComponent(org)}/invitations`,
      )
    ).invitations;
  }
  resendInvitation(org: string, id: string) {
    return this.request<InvitationView>(
      'POST',
      `/api/orgs/${encodeURIComponent(org)}/invitations/${encodeURIComponent(id)}/resend`,
    );
  }
  revokeInvitation(org: string, id: string) {
    return this.request<{ ok: true }>(
      'DELETE',
      `/api/orgs/${encodeURIComponent(org)}/invitations/${encodeURIComponent(id)}`,
    );
  }
  /** Public preview of an invitation link (no sign-in needed). */
  invitation(id: string) {
    return this.request<InvitationView>('GET', `/api/invitations/${encodeURIComponent(id)}`);
  }
  async domains(org: string) {
    return (
      await this.request<{ domains: OrgDomain[] }>('GET', `/api/orgs/${encodeURIComponent(org)}/domains`)
    ).domains;
  }
  createDomain(org: string, req: CreateOrgDomainRequest) {
    return this.request<OrgDomain>('POST', `/api/orgs/${encodeURIComponent(org)}/domains`, req);
  }
  deleteDomain(org: string, id: string) {
    return this.request<{ ok: true }>(
      'DELETE',
      `/api/orgs/${encodeURIComponent(org)}/domains/${encodeURIComponent(id)}`,
    );
  }
  async pendingUsers() {
    return (await this.request<{ users: PendingUser[] }>('GET', '/api/admin/pending-users')).users;
  }
  assignUser(userId: string, req: AssignUserRequest) {
    return this.request<{ ok: true }>('POST', `/api/admin/users/${encodeURIComponent(userId)}/assign`, req);
  }

  // --- projects ------------------------------------------------------------------------------
  /** The projects you can see, each with its newest publication, last activity and (staff) what waits. */
  async projects() {
    return (await this.request<{ projects: ProjectListItem[] }>('GET', '/api/projects')).projects;
  }
  project(ref: string) {
    return this.request<Project>('GET', `/api/projects/${encodeRef(ref)}`);
  }
  /** Addresses in Staðfangaskrá (HMS's register) by land number or text, with WGS84 points. */
  async addresses(query: AddressQuery) {
    return (
      await this.request<{ addresses: RegistryAddress[] }>(
        'GET',
        `/api/addresses${qs(query as Record<string, string>)}`,
      )
    ).addresses;
  }
  /** Change a project's name, place or map location (managers). */
  updateProject(ref: string, req: UpdateProjectRequest) {
    return this.request<Project>('PATCH', `/api/projects/${encodeRef(ref)}`, req);
  }
  createProject(org: string, input: CreateProjectRequest) {
    return this.request<Project>('POST', `/api/orgs/${encodeURIComponent(org)}/projects`, input);
  }
  async access(ref: string) {
    return (await this.request<{ access: AccessGrant[] }>('GET', `/api/projects/${encodeRef(ref)}/access`))
      .access;
  }
  async putAccess(ref: string, req: PutAccessRequest) {
    return (
      await this.request<{ access: AccessGrant[] }>('PUT', `/api/projects/${encodeRef(ref)}/access`, req)
    ).access;
  }

  // --- files ---------------------------------------------------------------------------------
  files(ref: string, query: ListFilesQuery = {}) {
    return this.request<{ files: FileEntry[]; total: number }>(
      'GET',
      `/api/projects/${encodeRef(ref)}/files${qs(query as Record<string, string>)}`,
    );
  }
  /** Every file matching the query, paging through the listing. */
  async allFiles(ref: string, query: Omit<ListFilesQuery, 'limit' | 'offset'> = {}): Promise<FileEntry[]> {
    const out: FileEntry[] = [];
    const limit = 5000;
    for (let offset = 0; ; offset += limit) {
      const page = await this.files(ref, { ...query, limit, offset });
      out.push(...page.files);
      if (page.files.length < limit || out.length >= page.total) return out;
    }
  }
  file(ref: string, path: string) {
    return this.request<FileEntry>('GET', `/api/projects/${encodeRef(ref)}/files/${encodePath(path)}?info`);
  }
  versions(ref: string, path: string) {
    return this.request<{ file: FileEntry; versions: FileVersion[] }>(
      'GET',
      `/api/projects/${encodeRef(ref)}/files/${encodePath(path)}/versions`,
    );
  }
  /** A short-lived presigned URL for the current version (or `version`). */
  download(ref: string, path: string, version?: number) {
    return this.request<DownloadInfo>(
      'GET',
      `/api/projects/${encodeRef(ref)}/files/${encodePath(path)}${qs({ version, redirect: 0 })}`,
    );
  }
  /** The API URL that redirects to the file (for links in a signed-in browser). */
  fileHref(ref: string, path: string, version?: number): string {
    return `${this.baseUrl}/api/projects/${encodeRef(ref)}/files/${encodePath(path)}${qs({ version })}`;
  }
  patchFile(ref: string, path: string, req: PatchFileRequest) {
    return this.request<FileEntry>('PATCH', `/api/projects/${encodeRef(ref)}/files/${encodePath(path)}`, req);
  }
  deleteFile(ref: string, path: string) {
    return this.request<FileEntry>('DELETE', `/api/projects/${encodeRef(ref)}/files/${encodePath(path)}`);
  }

  // --- uploads -------------------------------------------------------------------------------
  beginUpload(ref: string, req: BeginUploadRequest) {
    return this.request<BeginUploadResponse>('POST', `/api/projects/${encodeRef(ref)}/uploads`, req);
  }
  completeUpload(ref: string, uploadId: string, req: CompleteUploadRequest = {}) {
    return this.request<CompleteUploadResponse>(
      'POST',
      `/api/projects/${encodeRef(ref)}/uploads/${encodeURIComponent(uploadId)}/complete`,
      req,
    );
  }

  /**
   * Upload one file version: begin (the server may already hold the bytes), send the bytes to the bucket (one PUT
   * or parts), complete. `onProgress` gets bytes sent so far.
   */
  async upload(
    ref: string,
    req: BeginUploadRequest,
    source: ByteSource,
    opts: { onProgress?: (sent: number, total: number) => void; concurrency?: number } = {},
  ): Promise<{ file: FileEntry; version: FileVersion; deduplicated: boolean; unchanged: boolean }> {
    const begun = await this.beginUpload(ref, req);
    if (begun.status === 'done') {
      opts.onProgress?.(source.size, source.size);
      return {
        file: begun.file,
        version: begun.version,
        deduplicated: begun.deduplicated,
        unchanged: begun.unchanged,
      };
    }
    if (begun.status === 'upload') {
      const body = await source.read(0, source.size);
      const res = await this.doFetch(begun.url, {
        method: 'PUT',
        headers: begun.headers,
        body: body as unknown as RequestInit['body'],
      });
      if (!res.ok) {
        throw new P2bError(
          res.status,
          'put_failed',
          `The bucket refused the upload (${res.status}): ${await res.text()}`,
        );
      }
      opts.onProgress?.(source.size, source.size);
      const done = await this.completeUpload(ref, begun.uploadId);
      return { file: done.file, version: done.version, deduplicated: false, unchanged: false };
    }
    const parts: { partNumber: number; etag: string }[] = [];
    let sent = 0;
    const queue = [...begun.parts];
    const worker = async () => {
      for (let part = queue.shift(); part; part = queue.shift()) {
        const start = (part.partNumber - 1) * begun.partSize;
        const end = Math.min(start + begun.partSize, source.size);
        const body = await source.read(start, end);
        const res = await this.doFetch(part.url, {
          method: 'PUT',
          body: body as unknown as RequestInit['body'],
        });
        if (!res.ok)
          throw new P2bError(
            res.status,
            'put_failed',
            `Part ${part.partNumber} was refused (${res.status}).`,
          );
        const etag = res.headers.get('etag');
        if (!etag)
          throw new P2bError(500, 'no_etag', 'The bucket returned no ETag (expose it in its CORS settings).');
        parts.push({ partNumber: part.partNumber, etag });
        sent += end - start;
        opts.onProgress?.(sent, source.size);
      }
    };
    await Promise.all(Array.from({ length: Math.max(1, opts.concurrency ?? 3) }, worker));
    const done = await this.completeUpload(ref, begun.uploadId, { parts });
    return { file: done.file, version: done.version, deduplicated: false, unchanged: false };
  }

  // --- revisions and publications ------------------------------------------------------------
  async revisions(ref: string) {
    return (await this.request<{ revisions: Revision[] }>('GET', `/api/projects/${encodeRef(ref)}/revisions`))
      .revisions;
  }
  revision(ref: string, rev: string) {
    return this.request<Revision>(
      'GET',
      `/api/projects/${encodeRef(ref)}/revisions/${encodeURIComponent(rev)}`,
    );
  }
  createRevision(ref: string, manifest: RevisionManifestInput) {
    return this.request<Revision>('POST', `/api/projects/${encodeRef(ref)}/revisions`, { manifest });
  }
  /** Attach (or replace) a revision's model still: a JPEG, PNG or WebP picture. `view` records how it was rendered. */
  async putRevisionStill(ref: string, rev: string, picture: Uint8Array, view?: string | null) {
    const path = `/api/projects/${encodeRef(ref)}/revisions/${encodeURIComponent(rev)}/still${qs({ view })}`;
    const res = await this.doFetch(this.baseUrl + path, {
      method: 'PUT',
      headers: this.headers({ 'content-type': 'application/octet-stream' }),
      body: picture as unknown as RequestInit['body'],
      credentials: this.credentials,
      redirect: 'manual',
    });
    const data: any = await res.json().catch(() => null);
    if (!res.ok)
      throw new P2bError(
        res.status,
        data?.error?.code ?? `http_${res.status}`,
        data?.error?.message ?? `PUT ${path} failed with ${res.status}`,
        data?.error?.details,
      );
    return data as {
      revisionId: string;
      sha256: string;
      size: number;
      contentType: string;
      stillUrl: string;
    };
  }
  async publications(ref: string) {
    return (
      await this.request<{ publications: PublicationView[] }>(
        'GET',
        `/api/projects/${encodeRef(ref)}/publications`,
      )
    ).publications;
  }
  publish(ref: string, req: PublishRequest) {
    return this.request<PublicationView>('POST', `/api/projects/${encodeRef(ref)}/publications`, req);
  }
  withdraw(ref: string, publicationId: string) {
    return this.request<PublicationView>(
      'DELETE',
      `/api/projects/${encodeRef(ref)}/publications/${encodeURIComponent(publicationId)}`,
    );
  }
  /** A content base for a publication's pages (or "draft"): what the page shell loads in its frame. */
  pageSession(ref: string, publication: string) {
    return this.request<PageSession>('POST', `/api/projects/${encodeRef(ref)}/page-sessions`, {
      publication,
    });
  }
  publicationDownload(ref: string, publicationId: string, path: string) {
    return this.request<{ url: string }>(
      'GET',
      `/api/projects/${encodeRef(ref)}/publications/${encodeURIComponent(publicationId)}/downloads/${encodePath(path)}?redirect=0`,
    );
  }
  publicationDownloadHref(ref: string, publicationId: string, path: string): string {
    return `${this.baseUrl}/api/projects/${encodeRef(ref)}/publications/${encodeURIComponent(publicationId)}/downloads/${encodePath(path)}`;
  }
  async audit(ref: string, opts: { limit?: number; before?: number } = {}) {
    return (
      await this.request<{ events: AuditEvent[] }>('GET', `/api/projects/${encodeRef(ref)}/audit${qs(opts)}`)
    ).events;
  }
}
